/* ============================================================================
   agmf-seen.js — who has already been shown what, in the Media Flow feed.

   THIS FILE DECIDES NOTHING. It records what a browser has actually been
   shown, and it answers questions about that record. Whether a piece of
   content is shown again is a separate decision, made later, against the
   answers here — keeping the two apart is the point: a recording layer that
   also hides things cannot be trusted to have recorded honestly.

   WHY IT EXISTS
   The feed was the one content surface with no telemetry at all. Product
   cards have reported card_impression / card_seen for a long time (3.8M rows
   in fourteen days), reviews report review_seen, the Games popup reports
   games_popup_shown — but the feed, which recycles endlessly through 40-odd
   pieces of community photography, film and press, has never recorded that
   anybody saw any of it. So "don't show them that again" had nothing to stand
   on (audited 2026-08-27).

   WHAT IS RECORDED — a LADDER, not a boolean
     1  in view    the card was at least half on screen
     2  dwelled    …and stayed there (1s by default, set in the seen policy),
                so a person actually looked rather than scrolled past
     3  opened     they acted on it (read the article, played the film)
   A boolean would throw away the difference between a film somebody finished
   and one that merely scrolled past, and those two deserve opposite answers
   to "show it again?".

   IDENTITY (this build)
   Keyed on the BROWSER — `visitorID`, the same id every other storefront
   system uses. Clearing storage, a new device or a private window is a new
   browser and therefore a clean slate. Becoming person-level later means
   merging two browsers' sets when the vid↔email spine links them; nothing
   here has to change for that to happen. The identity FENCE below is what
   makes that safe: an adopted identity must never inherit the operator's
   record.

   WHAT IT KEYS ON
   `data-agmf-ck` — the canonical content key the exporter computes and the
   feed stamps on every card. Never the record id: fourteen YouTube items ship
   under the bare video id and one under `youtube-<id>`, so a record-keyed
   ledger would answer "no, never seen" about a film watched five minutes ago.

   ISOLATION PRINCIPLE
   Reads the DOM and window globals only. Emits through window.CAEDev, the
   same bridge cae-extra-events.js uses. Edits no upstream system; the feed's
   only involvement is the data attribute it already stamps for everyone.

   PUBLIC API (what a later "show it again?" rule will read)
     AGMF_Seen.has(ck)              → boolean
     AGMF_Seen.info(ck)             → {days, times, depth, type} | null
     AGMF_Seen.since(ck)            → whole days since last shown, or null
     AGMF_Seen.mark(ck, depth, type)→ record from elsewhere (e.g. 'opened')
     AGMF_Seen.all()  .size()  .forget(ck)  .reset()
   ============================================================================ */
(function () {
  'use strict';

  if (window.AGMF_Seen) return;                    // one owner per page

  var STORE   = 'AGB_ContentSeen';
  var VERSION = 1;
  // 500 was one 119-item feed times four product pages: a shopper who browsed
  // five products had their oldest memory evicted, and "already seen" quietly
  // became "never seen" again (audit 2026-09-06). 3,000 keys is ~180KB of
  // localStorage - a rounding error against the 5MB origin quota.
  var MAX_KEYS = 3000;
  // HOW LONG A STOP COUNTS AS A LOOK. One second (Dorian 2026-09-03: "Seen
  // content = 1s stop"); it was two, chosen by nobody. Read from the feed's
  // own seen policy each time a card appears rather than at boot: this file
  // loads with the section and the feed arrives over the network after it,
  // so a boot-time read would always take the fallback.
  var DWELL_FALLBACK_MS = 1000;
  function dwellMs() {
    try {
      var policy = window.AGMF_FEED && window.AGMF_FEED.seenPolicy;
      var seconds = policy && policy.dwellSeconds;
      if (typeof seconds === 'number' && seconds > 0) {
        return Math.min(10000, Math.max(200, Math.round(seconds * 1000)));
      }
    } catch (err) { /* a missing feed is not a reason to stop recording */ }
    return DWELL_FALLBACK_MS;
  }
  var VIEW_FRACTION = 0.5;   // a feed card is full-bleed; half of it is a real look
  var BATCH_FLUSH_AT = 25;   // don't stake a long session on the unload beacon

  var DEPTH = { view: 1, dwell: 2, opened: 3 };
  var DEPTH_NAME = { 1: 'in view', 2: 'dwelled', 3: 'opened' };

  function log() {
    try {
      if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
        console.log.apply(console, ['[agmf-seen]'].concat([].slice.call(arguments)));
      }
    } catch (e) {}
  }

  function today() { return Math.floor(Date.now() / 86400000); }

  function vid() {
    try {
      return localStorage.getItem('visitorID') ||
        (document.cookie.match(/(?:^|;\s*)visitorID=([^;]+)/) || [])[1] || '';
    } catch (e) { return ''; }
  }

  /* ---- the record ------------------------------------------------------- */
  var state = null;

  function nowIso() { return new Date().toISOString(); }

  function load() {
    var who = vid();
    var raw = null;
    try { raw = JSON.parse(localStorage.getItem(STORE) || 'null'); } catch (e) { raw = null; }
    // Shape guard: a half-written or hand-edited value must not brick the
    // feature. Anything unexpected is thrown away rather than reasoned about.
    if (!raw || raw.v !== VERSION || !raw.k || typeof raw.k !== 'object' ||
        Object.prototype.toString.call(raw.k) === '[object Array]') {
      return { v: VERSION, vid: who, b: nowIso(), k: {} };
    }
    // THE IDENTITY FENCE. A different visitor is a different person's record,
    // and the case that matters is deliberate: an operator viewing as somebody
    // else through the session guard must not inherit — or write into — their
    // own history. Same reasoning as reco-session's fence.
    if (raw.vid && who && raw.vid !== who) {
      log('identity changed — starting a fresh record');
      return { v: VERSION, vid: who, b: nowIso(), k: {} };
    }
    if (!raw.vid && who) raw.vid = who;
    return raw;
  }

  function save() {
    try {
      trim();
      localStorage.setItem(STORE, JSON.stringify(state));
    } catch (e) {
      // Quota, private mode, a browser that refuses storage. The feature
      // degrades to "remembers nothing", which is the behaviour that existed
      // before this file — never an error a shopper can see.
    }
  }

  function trim() {
    var keys = Object.keys(state.k);
    if (keys.length <= MAX_KEYS) return;
    // Oldest first, then least-seen. What survives is what was seen recently
    // or often — which is what a "show it again?" rule actually asks about.
    keys.sort(function (a, b) {
      var A = state.k[a], B = state.k[b];
      return (A.d - B.d) || (A.n - B.n);
    });
    var drop = keys.length - MAX_KEYS;
    for (var i = 0; i < drop; i++) delete state.k[keys[i]];
    log('trimmed', drop, 'oldest entries; keeping', MAX_KEYS);
  }

  /* ---- what to send to the ledger --------------------------------------- */
  // ONE event per page carrying every key, not one event per card. The feed
  // recycles endlessly, so a per-impression event would out-produce
  // card_impression's 3.8M rows/14d by a wide margin and buy nothing: the
  // question is "has this browser seen this content", and that is answered
  // just as well by a page-level roll-up.
  var pending = {};
  var pendingN = 0;
  var sent = 0;

  function record(ck, depth, type, surface) {
    if (!ck) return;
    var e = state.k[ck];
    if (!e) { e = state.k[ck] = { d: today(), n: 0, x: 0, t: type || '' }; }
    e.d = today();
    e.n += 1;
    if (depth > e.x) e.x = depth;
    if (type && !e.t) e.t = type;
    // WHICH SURFACE. The product card at the top of the page and the feed
    // underneath it draw on the same library, so the same picture can appear
    // on both. `c` remembers that this one was already shown on the CARD,
    // which is the only way the feed can decline to show it again.
    if (surface === 'card') e.c = 1;

    var p = pending[ck];
    if (!p) { p = pending[ck] = { k: ck, t: e.t, x: 0, c: 0 }; pendingN++; }
    p.c += 1;
    if (depth > p.x) p.x = depth;

    save();
    log(ck, DEPTH_NAME[depth] || depth, '· ' + e.n + 'x total');
    if (pendingN >= BATCH_FLUSH_AT) flush('batch');
  }

  function flush(why) {
    var keys = Object.keys(pending);
    if (!keys.length) return;
    var rows = keys.map(function (k) { return pending[k]; });
    pending = {}; pendingN = 0;
    // Chunked so one very long session cannot build a payload the ingest
    // service has to reject whole.
    for (var i = 0; i < rows.length; i += 60) {
      var chunk = rows.slice(i, i + 60);
      emit({
        surface: 'feed',
        product_handle: handle(),
        reason: why,
        n: chunk.length,
        seen: chunk               // NOT `items`: a field named `items` collides
                                  // with dict/Row .items() readers downstream
      });
    }
    sent += rows.length;
  }

  function emit(detail) {
    try {
      if (window.CAE_DISABLED === true) return;
      if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
        window.CAEDev.emit('content_seen', detail);
        log('reported', detail.n, 'key(s) —', detail.reason);
      }
    } catch (e) {}
  }

  function handle() {
    try {
      var m = location.pathname.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  /* ---- watching the feed ------------------------------------------------ */
  // One observer PER SURFACE - the feed and the product card are watched
  // separately, and a single shared slot would be overwritten by the second
  // call and leave the first surface's observer unreachable.
  var observers = [];
  var HAS_WEAKMAP = typeof WeakMap === 'function';
  var timers  = HAS_WEAKMAP ? new WeakMap() : null;   // element -> dwell timer
  var watched = HAS_WEAKMAP ? new WeakMap() : null;   // element -> already observed

  function ckOf(el) { return el && el.dataset ? (el.dataset.agmfCk || '') : ''; }
  function typeOf(el) { return el && el.dataset ? (el.dataset.agmfType || '') : ''; }
  function surfaceOf(el) { return el && el.dataset ? (el.dataset.agmfSurface || 'feed') : 'feed'; }

  function startWatching(section) {
    if (typeof IntersectionObserver !== 'function') {
      log('no IntersectionObserver — nothing recorded'); return;
    }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var en = entries[i], el = en.target, ck = ckOf(el);
        if (!ck) continue;
        if (en.isIntersecting && en.intersectionRatio >= VIEW_FRACTION) {
          // In view counts immediately; dwell only if it stays. A card the
          // shopper flicked past is a weaker fact than one they rested on,
          // and the ladder is the whole reason this is not a boolean.
          if (!seenThisView(el)) {
            markThisView(el);
            record(ck, DEPTH.view, typeOf(el), surfaceOf(el));
          }
          if (timers) {
            var t = setTimeout(function (e2, k2, ty2, sf2) {
              return function () { record(k2, DEPTH.dwell, ty2, sf2); };
            }(el, ck, typeOf(el), surfaceOf(el)), dwellMs());
            timers.set(el, t);
          }
        } else {
          if (timers && timers.has(el)) { clearTimeout(timers.get(el)); timers['delete'](el); }
          clearThisView(el);          // it may come back on a later lap
        }
      }
    }, { threshold: [VIEW_FRACTION] });

    observers.push(io);

    function observeAll() {
      var cards = section.querySelectorAll('[data-agmf-ck]');
      var added = 0;
      for (var i = 0; i < cards.length; i++) {
        if (watched && watched.has(cards[i])) continue;
        if (watched) watched.set(cards[i], 1);
        io.observe(cards[i]);
        added++;
      }
      return added;
    }
    observeAll();
    // The feed appends cycles forever, so new cards have to be picked up as
    // they arrive rather than once at boot.
    try {
      new MutationObserver(function () { observeAll(); })
        .observe(section, { childList: true, subtree: true });
    } catch (e) {}
  }

  // "already counted while it is on screen" — a per-element flag, cleared when
  // it leaves, so the SAME card reappearing on a later lap counts again. That
  // repeat is the most useful number here: it is literally how many times we
  // have shown somebody the same thing.
  function seenThisView(el) { return el.__agmfInView === true; }
  function markThisView(el) { el.__agmfInView = true; }
  function clearThisView(el) { el.__agmfInView = false; }

  /* ---- a reset asked for in Agilite Media -------------------------------- */

  // The memory lives HERE, in this browser, so nothing on a server can
  // clear it. What a server can do is say "somebody asked that this visitor
  // start over, at this time". Once per page (at most once a minute), and
  // only when there is a record worth resetting, the ledger asks Agilite
  // Media for the newest such request for its own visitor id. A request
  // newer than the record's birth - and newer than any request already
  // honoured - wipes it, the same way the identity fence does. Anything
  // failing here fails OPEN: the feed goes on remembering, never breaks.
  var AM = 'https://agilite-media-432657747828.europe-west1.run.app/';

  function honourReset() {
    var who = vid();
    if (!who || !state || !Object.keys(state.k).length) return;   // nothing to reset
    var stamp = 'agmf.resetAsked';
    try {
      if (Number(sessionStorage.getItem(stamp) || 0) > Date.now() - 60000) return;
      sessionStorage.setItem(stamp, String(Date.now()));
    } catch (e) { /* no sessionStorage: ask anyway, it is one small GET */ }
    try {
      fetch(AM + 'api/feed/reset?vid=' + encodeURIComponent(who), { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          var at = d && d.at;
          if (!at) return;
          if (state.b && at <= state.b) return;          // older than this record: moot
          if (state.r && at <= state.r) return;          // already honoured
          state = { v: VERSION, vid: who, b: nowIso(), r: at, k: {} };
          save();
          log('feed memory reset, as asked in Agilite Media at ' + at);
        })
        .catch(function () {});
    } catch (e) {}
  }

  /* ---- public API -------------------------------------------------------- */
  window.AGMF_Seen = {
    has: function (ck) { return !!(ck && state.k[ck]); },
    info: function (ck) {
      var e = ck && state.k[ck];
      if (!e) return null;
      return { days: today() - e.d, times: e.n, depth: e.x, type: e.t,
               card: e.c === 1 };
    },
    since: function (ck) {
      var e = ck && state.k[ck];
      return e ? (today() - e.d) : null;
    },
    mark: function (ck, depth, type, surface) {
      record(ck, DEPTH[depth] || Number(depth) || DEPTH.view, type, surface);
    },
    all: function () { return JSON.parse(JSON.stringify(state.k)); },
    size: function () { return Object.keys(state.k).length; },
    forget: function (ck) { delete state.k[ck]; save(); },
    reset: function () { state = { v: VERSION, vid: vid(), b: nowIso(), k: {} }; save(); },
    DEPTH: DEPTH,
    _debug: {
      pending: function () { return pendingN; },
      sent: function () { return sent; },
      flush: function () { flush('manual'); }
    }
  };

  /* ---- boot -------------------------------------------------------------- */
  function boot() {
    state = load();
    var section = document.querySelector('.agmf-section');
    if (!section) return;
    startWatching(section);
    // THE PRODUCT CARD IS A SURFACE TOO. It sits OUTSIDE the feed section -
    // it replaces the theme's gallery at the top of the page - so watching
    // the feed alone would record none of it, and the feed could never know
    // what the card had already shown.
    var hero = document.querySelector('.agmf-hero');
    if (hero) startWatching(hero);
    else {
      var heroWait = new MutationObserver(function () {
        var h = document.querySelector('.agmf-hero');
        if (h) { heroWait.disconnect(); startWatching(h); }
      });
      heroWait.observe(document.body || document.documentElement,
                       { childList: true, subtree: true });
      setTimeout(function () { heroWait.disconnect(); }, 20000);
    }
    honourReset();
    log('watching the feed · already knows', Object.keys(state.k).length, 'piece(s) of content');
  }

  // The feed is built asynchronously from a bucket fetch; wait for the section
  // rather than for DOMContentLoaded, which lands long before it exists.
  var tries = 0;
  (function wait() {
    if (document.querySelector('.agmf-section')) { boot(); return; }
    if (++tries > 60) return;                       // ~30s, then give up quietly
    setTimeout(wait, 500);
  })();

  // Report on the way out. visibilitychange is the reliable one on mobile;
  // pagehide covers the desktop close. Both are idempotent — flush() empties
  // what it sends.
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      flush('hidden');
      try { if (window.CAEDev && window.CAEDev.flushNow) window.CAEDev.flushNow(); } catch (e) {}
    }
  });
  window.addEventListener('pagehide', function () {
    flush('pagehide');
    try { if (window.CAEDev && window.CAEDev.flushNow) window.CAEDev.flushNow(); } catch (e) {}
  });
})();
